/**
 * Level one of back testing: the protocol's scenario types as a scannable index, each row a type's
 * description and how many past tickets and fabricated scenarios sit under it. The search filters the
 * list instantly and, past three characters, also asks the account-wide index for near matches,
 * including types under other SOPs.
 */

import type { KeyboardEvent, ReactNode, RefObject } from 'react';
import { ExternalLink, Loader2, Sparkles, Ticket } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { HighlightedText } from '@/components/ui/highlighted-text';
import { SearchInput } from '@/components/ui/input';
import { ContainedScrollArea } from '@/components/ui/contained-scroll-area';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import type { ScenarioCounts, ScenarioTypeMatch, TypeReconcileSummary } from '@/services/ai-function.service';
import type { TypeReconcileProgress } from '@/types/websocket.types';
import { backTestingUrl } from '@/constants/paths';
import { UNTYPED_TYPE_ID } from '../back-testing.store';
import { ReadinessBadge } from '../readiness/readiness-badge';
import type { TypeReadiness } from '../readiness/types';
import type { ScenarioType } from '../types';
import { countsFor, countsLine, totalOf } from './scenario-counts';
import { liveVerdict, typeMark, type LiveVerdict, type TypeMark } from './type-reconcile';

const MARK_VARIANTS: Record<TypeMark, 'info' | 'secondary' | 'warning'> = {
	new: 'info',
	updated: 'secondary',
	stale: 'warning'
};

const LIVE_TONES: Record<LiveVerdict, string> = {
	checking: 'animate-pulse text-gray-700',
	unchanged: 'text-gray-800',
	reworded: 'text-blue-700',
	'no longer fits': 'text-amber-700'
};

interface SemanticSearchState {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	anchorRef: RefObject<HTMLDivElement | null>;
	loading: boolean;
	unavailable: boolean;
	rows: { match: ScenarioTypeMatch; external: boolean }[];
	sameSopMatches: ScenarioTypeMatch[];
	otherSopMatches: ScenarioTypeMatch[];
	activeIndex: number;
	onActivate: (row: { match: ScenarioTypeMatch; external: boolean }) => void;
	onFocus: () => void;
}

/**
 * One semantic match in the search dropdown. Rows from other SOPs are real
 * links so modifier clicks keep working; rows of the open SOP select in place.
 */
function TypeMatchRow({
	match,
	href,
	active = false,
	id,
	onSelect
}: {
	match: ScenarioTypeMatch;
	href?: string;
	active?: boolean;
	id: string;
	onSelect?: () => void;
}) {
	const className = cn(
		'flex w-full cursor-pointer items-start gap-2 px-2.5 py-1.5 text-left transition-colors hover:bg-accent',
		active && 'bg-accent'
	);
	const body = (
		<>
			<span className='min-w-0 flex-1'>
				<span className='block truncate text-xs'>{match.description}</span>
				{href && (
					<span className='mt-0.5 flex items-center gap-1 text-[10px] text-muted-foreground'>
						<span className='capitalize'>{match.sopName}</span>
						<ExternalLink className='size-2.5' />
					</span>
				)}
			</span>
			<span className='mt-0.5 shrink-0 text-[10px] font-medium tabular-nums text-muted-foreground' title='Similarity'>
				{Math.round(match.score * 100)}%
			</span>
		</>
	);
	return href ? (
		<a id={id} role='option' aria-selected={active} href={href} target='_blank' rel='noopener noreferrer' className={className}>
			{body}
		</a>
	) : (
		<button id={id} role='option' aria-selected={active} type='button' onClick={onSelect} className={className}>
			{body}
		</button>
	);
}

/** A row of the list, in the simulator list row's tokens; a type's description takes up to two lines. */
const TypeRow = ({
	selected,
	tone = 'default',
	counts,
	readiness,
	mark,
	live,
	onSelect,
	children
}: {
	selected: boolean;
	/** The untyped group stands apart in amber. */
	tone?: 'default' | 'amber';
	counts: ScenarioCounts;
	/** A tested type's verdict; an untested one shows none, so the list stays quiet until a run settles. */
	readiness?: TypeReadiness;
	mark?: TypeMark | null;
	/** The running check's word on the type, until the compile saves. */
	live?: LiveVerdict | null;
	onSelect: () => void;
	children: ReactNode;
}) => (
	<button
		type='button'
		onClick={onSelect}
		aria-current={selected || undefined}
		className={cn(
			'flex w-full cursor-pointer items-start gap-3 border-b border-gray-300 px-4 py-3 text-left transition-colors',
			tone === 'amber' ? 'bg-amber-50/70 hover:bg-amber-50' : 'hover:bg-gray-100/60',
			selected &&
				(tone === 'amber'
					? 'shadow-[inset_2px_0_0_var(--color-amber-600)]'
					: 'bg-gray-100 shadow-[inset_2px_0_0_var(--color-ink)] hover:bg-gray-100')
		)}
	>
		<span className='line-clamp-2 min-w-0 flex-1 text-[13px] leading-4.5 font-medium text-text-body'>
			{mark && (
				<Badge variant={MARK_VARIANTS[mark]} className='mr-1.5 px-1.5 py-0 align-text-bottom text-[10px]'>
					{mark}
				</Badge>
			)}
			{children}
		</span>
		{live && <span className={cn('shrink-0 text-[11px] leading-4.5', LIVE_TONES[live])}>{live}</span>}
		{readiness && readiness.verdict !== 'no_run' && <ReadinessBadge readiness={readiness} className='shrink-0 text-[10px]' />}
		{totalOf(counts) > 0 && (
			<span
				className={cn(
					'flex shrink-0 items-center gap-2 text-[11px] leading-4.5 tabular-nums',
					tone === 'amber' ? 'text-amber-800' : 'text-gray-800'
				)}
				title={countsLine(counts, true)}
			>
				<span className='flex items-center gap-1'>
					<Ticket className='size-3' aria-hidden />
					{counts.ticket}
				</span>
				{counts.fabricated > 0 && (
					<span className='flex items-center gap-1 text-violet-700'>
						<Sparkles className='size-3' aria-hidden />
						{counts.fabricated}
					</span>
				)}
			</span>
		)}
	</button>
);

interface TypesListProps {
	/** What the search left visible. */
	types: ScenarioType[];
	/** The whole list, before the search filters it. */
	allTypes: ScenarioType[];
	terms: string[];
	/** Server scenario counts keyed by type id, the untyped group's under `none`; a type with none is simply absent. */
	countsByType: Record<string, ScenarioCounts>;
	/** Promotion verdicts keyed by type id; empty until they load. */
	readinessByType?: Record<string, TypeReadiness>;
	lastReconcile?: TypeReconcileSummary | null;
	/** A compile's check running over these types; their last marks wait until it saves. */
	reconciling?: TypeReconcileProgress | null;
	generating: boolean;
	search: string;
	onSearchChange: (value: string) => void;
	onSearchKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
	semantic: SemanticSearchState;
	streamEndRef: RefObject<HTMLDivElement | null>;
	/** The row being read beside the list; the list stays, so it has to say which. */
	selectedId?: string | null;
	onSelect: (typeId: string) => void;
	onSelectGap: () => void;
}

export const TypesList = ({
	types,
	allTypes,
	terms,
	countsByType,
	readinessByType,
	lastReconcile = null,
	reconciling = null,
	generating,
	search,
	onSearchChange,
	onSearchKeyDown,
	semantic,
	streamEndRef,
	selectedId,
	onSelect,
	onSelectGap
}: TypesListProps) => (
	<div className='flex min-h-0 flex-1 flex-col'>
		{allTypes.length > 0 && (
			<div className='shrink-0 border-b border-gray-500 p-3'>
				<Popover open={semantic.open} onOpenChange={semantic.onOpenChange}>
					<PopoverAnchor asChild>
						<div ref={semantic.anchorRef} className='relative'>
							<SearchInput
								value={search}
								onChange={(e) => onSearchChange(e.target.value)}
								onFocus={semantic.onFocus}
								onKeyDown={onSearchKeyDown}
								placeholder='Search scenario types'
								className='h-7 w-full text-xs'
								role='combobox'
								aria-expanded={semantic.open}
								aria-controls='type-search-results'
								aria-activedescendant={
									semantic.activeIndex >= 0 ? `type-search-option-${semantic.activeIndex}` : undefined
								}
							/>
							{semantic.loading && (
								<Loader2 className='absolute top-1/2 right-2.5 size-3 -translate-y-1/2 animate-spin text-muted-foreground' />
							)}
						</div>
					</PopoverAnchor>
					<PopoverContent
						align='start'
						sideOffset={4}
						onOpenAutoFocus={(e) => e.preventDefault()}
						// Typing must go on in the input: a click back into it is not "outside".
						onInteractOutside={(e) => {
							if (semantic.anchorRef.current?.contains(e.target as Node)) e.preventDefault();
						}}
						id='type-search-results'
						role='listbox'
						className='max-h-80 w-[var(--radix-popover-trigger-width)] overflow-y-auto p-0'
					>
						{semantic.unavailable ? (
							<p className='px-2.5 py-2 text-xs text-muted-foreground'>
								Semantic search isn&apos;t set up on this environment yet, so only this protocol&apos;s own types are filtered.
							</p>
						) : semantic.rows.length === 0 ? (
							<p className='px-2.5 py-2 text-xs text-muted-foreground'>No similar scenario types.</p>
						) : (
							<>
								{semantic.sameSopMatches.length > 0 && (
									<div className='py-1'>
										<p className='px-2.5 pt-1 pb-0.5 text-[10px] font-medium tracking-wide text-muted-foreground uppercase'>
											In this SOP
										</p>
										{semantic.sameSopMatches.map((match, index) => (
											<TypeMatchRow
												key={match.typeId}
												id={`type-search-option-${index}`}
												match={match}
												active={semantic.activeIndex === index}
												onSelect={() => semantic.onActivate({ match, external: false })}
											/>
										))}
									</div>
								)}
								{semantic.otherSopMatches.length > 0 && (
									<div className={cn('bg-muted/40 py-1', semantic.sameSopMatches.length > 0 && 'border-t')}>
										<p className='px-2.5 pt-1 pb-0.5 text-[10px] font-medium tracking-wide text-muted-foreground uppercase'>
											From other SOPs
										</p>
										{semantic.otherSopMatches.map((match, index) => (
											<TypeMatchRow
												key={`${match.sopId}:${match.typeId}`}
												id={`type-search-option-${semantic.sameSopMatches.length + index}`}
												match={match}
												href={backTestingUrl(match.sopId, match.typeId)}
												active={semantic.activeIndex === semantic.sameSopMatches.length + index}
											/>
										))}
									</div>
								)}
							</>
						)}
					</PopoverContent>
				</Popover>
			</div>
		)}

		<ContainedScrollArea>
			<div className='flex flex-col'>
				{totalOf(countsFor(countsByType, UNTYPED_TYPE_ID)) > 0 && !generating && (
					<TypeRow
						tone='amber'
						selected={selectedId === UNTYPED_TYPE_ID}
						counts={countsFor(countsByType, UNTYPED_TYPE_ID)}
						onSelect={onSelectGap}
					>
						<span className='text-amber-900'>Past tickets with no scenario type</span>
					</TypeRow>
				)}

				{types.length === 0 && allTypes.length > 0 && !generating && (
					<p className='px-4 py-3 text-xs leading-4 text-gray-800'>No scenario type matches that search.</p>
				)}

				{types.map((type) => (
					<TypeRow
						key={type.id}
						selected={selectedId === type.id}
						counts={countsFor(countsByType, type.id)}
						readiness={readinessByType?.[type.id]}
						mark={reconciling ? null : typeMark(type, lastReconcile)}
						live={liveVerdict(type, reconciling)}
						onSelect={() => onSelect(type.id)}
					>
						<HighlightedText text={type.description} terms={terms} />
					</TypeRow>
				))}

				{generating && (
					<div className='flex items-center gap-2 px-4 py-3 text-xs text-gray-800'>
						<Loader2 className='size-3.5 animate-spin' />
						Reading the protocol for the next scenario type…
					</div>
				)}
				<div ref={streamEndRef} />
			</div>
		</ContainedScrollArea>
	</div>
);
